import classNames from 'classnames'
import keycodes from 'keycodes'
import * as React from 'react'
import type {FocusEvent, KeyboardEvent, MouseEvent} from 'react'
import ReactDOM from 'react-dom'
import {tabbable} from 'tabbable'

import {autobind} from '@base/decorators'
import {findScrollableNode} from '@base/dom-utils/isScrollable'
import later from '@base/later'
import {
  createOrCallElement,
  cloneCreateOrCall,
  cloneCreateOrCallChildren,
  pickProps,
} from '@base/react-utils'

import CSSAnimation, {
  resolveAnimation,
} from '@base-uikit/animations/CSSAnimation'
import type {AnimationType} from '@base-uikit/animations/CSSAnimation'
import Boundary, {
  boundaryDecorator,
  isInBoundary,
  isNodeInBoundary,
} from '@base-uikit/boundaries/Boundary'
import FocusBoundary from '@base-uikit/boundaries/FocusBoundary'
import {PopoverBackdrop} from '@base-uikit/layers/backdrops'
import {OutsideClickCapture} from '@base-uikit/layers/eventCapture'
import type {TetherPositionProps} from '@base-uikit/tether/TetherPosition'
import type {CalculateResult} from '@base-uikit/tether/types'

import css from './PopoverController.css'

export type CloseOutOfViewEnum = true | 'both' | 'target' | 'popover' | 'any'
export type ToggleEnum = true | false | 'open' | 'close'

export type PopoverControllerProps = {
  opened: boolean | void | null
  // Renders popover balloon with contents. All props should be forwarded to the balloon
  renderPopover: (props: any) => React.ReactElement<any>
  // Renders position (eg. TetherPosition). All props should be forwarded to component!
  renderPosition: (props: any) => React.ReactElement<any>
  backdrop?: null | false | React.ComponentType<any>
  wrapper?: 'div' | 'span' | 'foreignObject' | React.ComponentType<any>
  focusBoundaryAt?: false | 'target' | 'popover'
  portal?: null | React.ComponentType<any> | (() => React.ReactElement<any>)
  animation?: AnimationType | null
  toggleOnHover?: boolean | number
  toggleOnFocus?: ToggleEnum
  toggleOnClick?: ToggleEnum
  // Toggles popover when [Enter] is pressed, defaults to toggleOnClick
  toggleOnEnter?: ToggleEnum
  closeOnOutsideClick?: boolean
  // Closes popover if both popover and/or target goes completely out-of-view
  // TRUE requires that BOTH popover & target go out of view
  closeOutOfView?: false | CloseOutOfViewEnum
  closeOnEscape?: boolean
  // when TRUE, will position popover within the closest scroll parent
  limitPopoverToScrollParent?: boolean
  // when TRUE, will use the closest scroll parent to handle closeOutOfView
  limitTargetToScrollParent?: boolean
  children?: React.ReactElement<any> | null
  className?: string
  style?: any
  onToggle: (opened: boolean) => void
  // Triggered when closing animation is finished
  onDidClose?: (() => void) | null
  onPosition?: ((a: CalculateResult) => void) | null
  onOutsideClick?: ((a: MouseEvent) => void) | null
  onKeyDown?: ((a: KeyboardEvent) => void) | null
  onClick?: ((a: MouseEvent) => void) | null
  keepInDOM?: boolean
}

function shouldToggle(option, opened) {
  if (option === true || option === false) {
    return option
  }
  if (!opened && option === 'open') {
    return true
  }
  if (opened && option === 'close') {
    return true
  }
  return false
}

// @ts-ignore Ignored during migration
// eslint-disable-next-line react/no-unsafe
@boundaryDecorator({boundaryName: 'PopoverController'})
class PopoverController extends React.Component<
  PopoverControllerProps,
  {
    closing: boolean
    popoverPlacement: string | undefined | null
    popoverAnchor: number | undefined | null
  }
> {
  static displayName = 'PopoverController'

  // from @boundaryDecorator
  // @ts-ignore Ignored during migration
  boundaries: string[]
  boundaryProps: any

  popoverRef = React.createRef()
  triggerWrapperRef = React.createRef()
  unmounted: boolean | undefined | null

  state: {
    closing: boolean
    popoverPlacement: string | undefined | null
    popoverAnchor: number | undefined | null
  } = {
    closing: false,
    popoverPlacement: null,
    popoverAnchor: null,
  }

  popoverDidCloseLater = later(this.popoverDidClose.bind(this))
  openLater = later(this.open.bind(this))

  UNSAFE_componentWillReceiveProps(props: PopoverControllerProps) {
    if (
      props.opened === false &&
      this.props.opened === true &&
      this.props.animation
    ) {
      const timeout = resolveAnimation(this.props.animation).exitTimeout
      if (timeout) {
        this.setState({closing: true})
        this.popoverDidCloseLater.delay(parseInt(timeout, 10) + 20)
      } else if (this.props.onDidClose) {
        this.props.onDidClose()
      }
    }
  }

  componentWillUnmount() {
    this.unmounted = true
    this.openLater.destroy()
    this.popoverDidCloseLater.destroy()
  }

  isOpened() {
    return this.props.opened
  }

  @autobind
  open() {
    this.openLater.cancel()
    if (!this.isOpened()) {
      this.props.onToggle(true)
    }
  }

  @autobind
  close() {
    this.openLater.cancel()
    if (this.isOpened()) {
      this.props.onToggle(false)
    }
  }

  @autobind
  toggle() {
    if (this.isOpened()) {
      this.close()
    } else {
      this.open()
    }
  }

  /** Returns TRUE if node belongs to popover or trigger */
  isNodePartOfController(node: any) {
    return isNodeInBoundary(node, this.boundaries)
  }

  /** Returns TRUE if node belongs to popover only */
  isNodePartOfPopover(node: any) {
    const {popoverRef} = this
    const popoverNode: any =
      // @ts-ignore Ignored during migration
      popoverRef.current && ReactDOM.findDOMNode(popoverRef.current)
    if (!popoverNode) {
      return false
    }
    const isInPopover = isInBoundary(popoverNode)
    return isInPopover(node)
  }

  /** Returns TRUE if node belongs to trigger only */
  isNodePartOfTrigger(node: any) {
    return this.isNodePartOfController(node) && !this.isNodePartOfPopover(node)
  }

  /** Returns TRUE if node belongs to trigger and is in the same DOM tree (doesn't go through portal) */
  isNodeContainedInTrigger(node: any) {
    const {triggerWrapperRef} = this
    const triggerNode: any =
      triggerWrapperRef.current &&
      // @ts-ignore Ignored during migration
      ReactDOM.findDOMNode(triggerWrapperRef.current)
    if (!triggerNode) {
      return false
    }
    return this.isNodePartOfTrigger(node) && triggerNode.contains(node)
  }

  @autobind
  popoverDidClose() {
    this.setState({closing: false})
    if (this.props.onDidClose) {
      this.props.onDidClose()
    }
  }

  @autobind
  handleMouseEnter(event: MouseEvent) {
    // As React bubbles up "onMouseEnter" through portals, we will receive onMouseEnter
    // for nodes deep inside the trigger. This includes events from other popovers.
    // This doesn't work well if we wrap a dropdown within a tooltip, as the tooltip will stay
    // opened while user is in the dropdown.
    // As this is a regression after React 16 bump, we're restoring the previous behaviour, where
    // the DOM was defining event bubbling boundaries.
    if (
      this.isNodeContainedInTrigger(event.target) ||
      this.isNodePartOfPopover(event.target)
    ) {
      const {toggleOnHover} = this.props
      if (toggleOnHover === true) {
        this.openLater.now()
      } else if (typeof toggleOnHover === 'number' && toggleOnHover > 0) {
        this.openLater.delay(toggleOnHover)
      }
    } else {
      this.handleMouseLeave(event)
    }
  }

  @autobind
  handleMouseLeave(event: MouseEvent) {
    this.close()
  }

  @autobind
  handleFocus(event: FocusEvent) {
    // As React bubbles up "onFocus" through portals, we will receive onMouseEnter
    // for nodes deep inside the trigger. This includes events from other popovers.
    // This doesn't work well if we wrap a dropdown within a tooltip, as the tooltip will stay
    // opened while user is in the dropdown.
    // As this is a regression after React 16 bump, we're restoring the previous behaviour, where
    // the DOM was defining event bubbling boundaries.
    if (
      this.isNodeContainedInTrigger(event.target) ||
      this.isNodePartOfPopover(event.target)
    ) {
      this.open()
    } else {
      this.handleBlur(event)
    }
  }

  @autobind
  handleBlur(event: FocusEvent) {
    this.close()
  }

  @autobind
  handleClick(event: MouseEvent) {
    if (!event.defaultPrevented) {
      const {onClick} = this.props

      // Ignore clicks from enter key, these are handled in keyDown
      if (!event.clientX && !event.clientY) {
        return
      }

      if (this.isNodePartOfTrigger(event.target as any)) {
        event.preventDefault()
        this.toggle()
      }

      if (onClick) {
        onClick(event)
      }
    }
  }

  @autobind
  handleOutsideClick(event: MouseEvent) {
    const {onOutsideClick} = this.props
    // skip if clicked on the trigger
    if (this.isNodePartOfController(event.target as any)) {
      return false
    }
    if (onOutsideClick) {
      onOutsideClick(event)
    }
    this.close()
    return true
  }

  @autobind
  handleBackdropClick(event: MouseEvent) {
    if (this.props.onOutsideClick) {
      this.props.onOutsideClick(event)
    }
    if (!(this.props.closeOnOutsideClick === false)) {
      this.close()
    }
  }

  @autobind
  handleKeyDown(event: KeyboardEvent) {
    if (!event.defaultPrevented) {
      const opened = this.isOpened()
      const {
        closeOnEscape,
        toggleOnHover,
        toggleOnClick,
        toggleOnEnter = toggleOnClick,
        onKeyDown,
      } = this.props

      if (
        event.keyCode === keycodes('enter') &&
        shouldToggle(toggleOnEnter, opened)
      ) {
        if (this.isNodePartOfTrigger(event.target as any)) {
          event.preventDefault()
          this.toggle()
        }
      }

      if (
        closeOnEscape &&
        Boolean(toggleOnHover) === false &&
        opened &&
        event.keyCode === keycodes('escape')
      ) {
        event.preventDefault()
        this.close()
      }

      if (onKeyDown) onKeyDown(event)
    }
  }

  isOutOfView(mode: CloseOutOfViewEnum, position: CalculateResult) {
    const popoverOOV = position.rating <= 0
    const targetOOV = position.targetRating <= 0
    switch (mode) {
      case true:
      case 'both':
        return popoverOOV && targetOOV
      case 'popover':
        return popoverOOV
      case 'target':
        return targetOOV
      case 'any':
        return targetOOV || popoverOOV
      default:
        return false
    }
  }

  @autobind
  handlePosition(position: CalculateResult) {
    const {onPosition, closeOutOfView} = this.props
    if (closeOutOfView && this.isOutOfView(closeOutOfView, position)) {
      this.close()
    }
    // can be NaN when keepInDOM Popover prop is true
    const parsedAnchorValue =
      Number(position.anchorX) || Number(position.anchorY)
    this.setState({
      popoverPlacement: position.placement,
      popoverAnchor: isNaN(parsedAnchorValue) ? null : parsedAnchorValue,
    })
    if (onPosition) onPosition(position)
  }

  @autobind
  getReturnFocus(lastActiveElement: any) {
    const nowhereToGo =
      !lastActiveElement || lastActiveElement === document.body
    // If there's nowhere to return to, try focusing on the target
    if (nowhereToGo && !this.unmounted) {
      const node = ReactDOM.findDOMNode(this)
      // @ts-ignore Ignored during migration
      return tabbable(node, {includeContainer: true})[0] || node
    }
    return lastActiveElement
  }

  @autobind
  renderPopoverElement(popoverProps: any) {
    const {renderPopover} = this.props
    // it's always safer to use cloneOrCall in case somebody passes a component here
    return createOrCallElement(renderPopover, {
      ...popoverProps,
      placement: this.state.popoverPlacement,
      anchor: this.state.popoverAnchor,
      onKeyDown: this.handleKeyDown,
      tabIndex: -1,
    })
  }

  renderPopoverBackdrop(popover: any) {
    let backdropComponent

    if (this.props.backdrop === undefined) {
      return null
    }

    if (typeof this.props.backdrop === 'boolean' && this.props.backdrop) {
      backdropComponent = PopoverBackdrop
    } else {
      backdropComponent = this.props.backdrop
    }

    return cloneCreateOrCall(backdropComponent, {
      onClick: this.handleBackdropClick,
      children: popover,
    })
  }

  renderPopoverPosition(popover: any) {
    const {
      renderPosition,
      limitPopoverToScrollParent,
      limitTargetToScrollParent,
    } = this.props
    const getThisNode = () => {
      const node = ReactDOM.findDOMNode(this)
      if (!(node instanceof Element)) {
        throw new Error('popover requires a DOM node as target')
      }
      return node
    }

    const positionProps: Partial<TetherPositionProps> = {
      onPosition: this.handlePosition,
      target: getThisNode,
      children: popover,
    }

    if (limitPopoverToScrollParent) {
      // @ts-ignore Ignored during migration
      positionProps.container = () =>
        findScrollableNode(getThisNode()) || window
    }

    if (limitTargetToScrollParent) {
      // @ts-ignore Ignored during migration
      positionProps.targetContainer = () =>
        findScrollableNode(getThisNode()) || window
    }

    return createOrCallElement(renderPosition, positionProps)
  }

  renderPopover() {
    const {
      portal,
      closeOnOutsideClick,
      animation,
      backdrop,
      focusBoundaryAt,
      opened,
      keepInDOM,
    } = this.props

    // Once rendered, Boundary will invoke renderPopoverElement with boundary props
    let popover: React.ReactElement | null = (
      // @ts-ignore Ignored during migration
      <Boundary boundaryName="popover" ref={this.popoverRef}>
        {this.renderPopoverElement}
      </Boundary>
    )

    // FocusBoundary has to be mounted only once TetherPosition finishes it's layout job. Otherwise we'll be focusing
    // invisible component. Also, we need to put a boundary *inside* a backdrop, so that it doesn't include the
    // backdrop background
    if (focusBoundaryAt === 'popover') {
      popover = (
        <FocusBoundary returnFocus={this.getReturnFocus}>
          {popover}
        </FocusBoundary>
      )
    }

    if (animation) {
      popover = (
        <CSSAnimation
          type={this.state.closing ? 'exit' : 'appear'}
          // carry-over style from popover, as it will be overwritten by TetherPosition
          style={popover.props.style}
          animation={animation}
        >
          {popover}
        </CSSAnimation>
      )
    }

    popover = this.renderPopoverPosition(popover)

    if (backdrop) {
      popover = this.renderPopoverBackdrop(popover)
    } else if (closeOnOutsideClick) {
      popover = (
        <OutsideClickCapture onEvent={this.handleOutsideClick}>
          {popover}
        </OutsideClickCapture>
      )
    }

    if (keepInDOM) {
      popover = (
        <div
          className={classNames({[css.hide]: !(opened || this.state.closing)})}
        >
          {popover}
        </div>
      )
    }

    if (portal) {
      popover = createOrCallElement(portal, {children: popover})
    }

    return popover
  }

  render() {
    const {
      children,
      style,
      className,
      opened,
      toggleOnHover,
      toggleOnFocus,
      toggleOnClick,
      backdrop,
      wrapper = 'span',
      focusBoundaryAt,
      onClick,
      keepInDOM,
    } = this.props

    const popoverElement =
      opened || this.state.closing || keepInDOM ? this.renderPopover() : null

    const childrenProps = pickProps(this.props, [
      'tabIndex',
      'autoFocus',
      'siblings',
    ])
    const containerProps = pickProps(this.props, /^data-/)

    const wrapperElement = createOrCallElement(wrapper, {
      ...containerProps,
      ref: this.triggerWrapperRef,
      className: css.PopoverController + ' ' + (className || ''),
      style,
      onMouseEnter: Number(toggleOnHover) > 0 ? this.handleMouseEnter : null,
      onMouseLeave: Number(toggleOnHover) > 0 ? this.handleMouseLeave : null,
      onFocus: shouldToggle(toggleOnFocus, false) ? this.handleFocus : null,
      onBlur: shouldToggle(toggleOnFocus, true) ? this.handleBlur : null,
      onClick: shouldToggle(toggleOnClick, opened) ? this.handleClick : onClick,
      onKeyDown: this.handleKeyDown,
      ...this.boundaryProps,
      children: (
        <React.Fragment>
          {children && cloneCreateOrCallChildren(children, childrenProps)}
          {popoverElement}
        </React.Fragment>
      ),
    })

    if (focusBoundaryAt === 'target') {
      if (backdrop) {
        throw new Error(
          'FocusBoundary at target cannot be used with backdrops!'
        )
      }
      return (
        <FocusBoundary returnFocus={this.getReturnFocus} active={!!opened}>
          {wrapperElement}
        </FocusBoundary>
      )
    }

    return wrapperElement
  }
}

export default PopoverController
